/* eslint-disable react-refresh/only-export-components -- bewusst co-lozierter Provider+Hook bzw. Helfer-Export; react-refresh ist eine reine Dev-HMR-Optimierung, kein Prod-/Qualitaetsthema. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Optionaler Click-Sound für interaktive Elemente.
 *
 * - Generiert via WebAudio API (kein Asset-Loading nötig).
 * - Toggle persistiert in localStorage; default: OFF (User aktiviert bewusst).
 * - Respektiert `prefers-reduced-motion` als zusätzlichen Override.
 *
 * Verwendung in Komponenten:
 *   const { playClick } = useSound();
 *   <button onClick={() => { playClick(); ...; }}>
 *
 * Oder als Auto-Trigger über `data-soundclick`-Attribut auf jedem Button:
 *   <button data-soundclick>…</button> — wird vom globalen Listener gespielt.
 */

interface SoundContextValue {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  playClick: () => void;
  /** true, wenn der Ton trotz aktivem Toggle stumm bleibt (OS: Bewegung reduzieren). */
  mutedByReducedMotion: boolean;
}

const SoundContext = createContext<SoundContextValue | null>(null);

const STORAGE_KEY = 'ignisai.sound.enabled';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

let audioCtx: AudioContext | null = null;
function ensureAudioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (audioCtx) return audioCtx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  audioCtx = new Ctor();
  return audioCtx;
}

function playTone(ctx: AudioContext): void {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  // Sehr kurzer, hoher Click — angenehm und nicht aufdringlich.
  osc.type = 'sine';
  osc.frequency.setValueAtTime(2200, now);
  osc.frequency.exponentialRampToValueAtTime(1400, now + 0.04);

  // ADSR vereinfacht: ultra-schneller Attack, kurzer Decay.
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.08, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.06);
}

function generateClickSound(): void {
  const ctx = ensureAudioCtx();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    // Autoplay-Policy: ein suspendierter Context läuft erst nach `resume()`.
    // Der Ton wird deshalb ERST im Resume-Callback geplant — Events, die man
    // vorher schreibt, hängen an einer eingefrorenen `currentTime` und können
    // verfallen. `resume()` wirkt nur aus einer Nutzergeste heraus, und genau
    // von dort kommt jeder Aufruf hier (Klick-Handler / Klick-Listener).
    void ctx
      .resume()
      .then(() => playTone(ctx))
      .catch(() => undefined);
    return;
  }

  playTone(ctx);
}

/**
 * Erzeugt bzw. weckt den AudioContext innerhalb der Nutzergeste, die den Sound
 * einschaltet. Ohne diesen Schritt entsteht der Context erst beim ersten
 * Ton-Versuch — und Browser starten ihn dann je nach Situation suspendiert.
 */
function unlockAudio(): void {
  const ctx = ensureAudioCtx();
  if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
}

function readStoredEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function readReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

export function SoundProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(readStoredEnabled);
  const [reducedMotion, setReducedMotion] = useState<boolean>(readReducedMotion);

  // `playClick` muss STABIL sein und den JEWEILS AKTUELLEN Stand lesen: es wird
  // u. a. in demselben Klick-Handler aufgerufen, der den Sound gerade
  // einschaltet. Ein über den State geschlossener Callback sähe dort noch den
  // alten Wert (`enabled === false`) und bliebe stumm.
  const enabledRef = useRef(enabled);
  const reducedMotionRef = useRef(reducedMotion);

  useEffect(() => {
    const mql = window.matchMedia?.(REDUCED_MOTION_QUERY);
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches;
      setReducedMotion(e.matches);
    };
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  const setEnabled = useCallback((v: boolean) => {
    enabledRef.current = v;
    setEnabledState(v);
    if (v) unlockAudio();
    try {
      window.localStorage.setItem(STORAGE_KEY, v ? 'true' : 'false');
    } catch {
      /* private mode */
    }
  }, []);

  const playClick = useCallback(() => {
    if (!enabledRef.current || reducedMotionRef.current) return;
    try {
      generateClickSound();
    } catch {
      /* fail silently */
    }
  }, []);

  // Globaler Listener für [data-soundclick]-Elemente. Spart Repetition in jedem
  // Button-Aufruf — wer den Click haben will, schreibt nur das Attribut hin.
  // Registrierung ist bewusst dauerhaft: `playClick` prüft den aktuellen Stand
  // selbst, so kann kein Klick zwischen State-Update und Re-Registrierung fallen.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-soundclick]')) playClick();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [playClick]);

  const value = useMemo<SoundContextValue>(
    () => ({
      enabled,
      setEnabled,
      playClick,
      mutedByReducedMotion: enabled && reducedMotion,
    }),
    [enabled, setEnabled, playClick, reducedMotion],
  );

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
}

export function useSound(): SoundContextValue {
  const ctx = useContext(SoundContext);
  if (!ctx) throw new Error('useSound must be used within a SoundProvider');
  return ctx;
}
